import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import MainLayout from "@/components/layout/MainLayout";
import { AlertCircle, Check, ChevronLeft, ChevronRight, CreditCard, File, FileCheck, FileText, Upload, User, Building, Home, ArrowLeft } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const ProponenteRegistro: React.FC = () => {
  const navigate = useNavigate();
  
  // Estado para controlar a etapa atual do formulário
  const [etapaAtual, setEtapaAtual] = useState<number>(1);
  
  // Estados para os campos do formulário
  const [tipoDocumento, setTipoDocumento] = useState<string>('cpf');
  const [documento, setDocumento] = useState<string>('');
  const [nome, setNome] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [telefone, setTelefone] = useState<string>('');
  const [senha, setSenha] = useState<string>('');
  const [confirmarSenha, setConfirmarSenha] = useState<string>('');
  
  // Estados para os arquivos
  const [estatuto, setEstatuto] = useState<File | null>(null);
  const [contratoSocial, setContratoSocial] = useState<File | null>(null);
  const [cartaoCnpj, setCartaoCnpj] = useState<File | null>(null);
  
  // Estados para cartão de crédito
  const [numeroCartao, setNumeroCartao] = useState<string>('');
  const [nomeCartao, setNomeCartao] = useState<string>('');
  const [validadeCartao, setValidadeCartao] = useState<string>('');
  const [cvvCartao, setCvvCartao] = useState<string>('');
  
  // Estado para verificar se o cadastro foi concluído
  const [cadastroConcluido, setCadastroConcluido] = useState<boolean>(false);
  
  // Função para validar o CPF/CNPJ
  const validarDocumento = (): boolean => {
    if (tipoDocumento === 'cpf') {
      return documento.length === 14; // xxx.xxx.xxx-xx
    } else {
      return documento.length === 18; // xx.xxx.xxx/xxxx-xx
    }
  };
  
  // Função para validar o formulário da primeira etapa
  const validarEtapa1 = (): boolean => {
    return (
      validarDocumento() &&
      nome.trim().length > 3 &&
      email.includes('@') &&
      telefone.length >= 10 &&
      senha.length >= 6 &&
      senha === confirmarSenha
    );
  };
  
  // Função para validar o formulário da segunda etapa
  const validarEtapa2 = (): boolean => {
    if (tipoDocumento === 'cnpj') {
      return !!estatuto && !!contratoSocial && !!cartaoCnpj;
    }
    return true; // Para CPF, não são necessários documentos
  };
  
  // Função para validar o formulário da terceira etapa
  const validarEtapa3 = (): boolean => {
    return (
      numeroCartao.replace(/\s/g, '').length === 16 &&
      nomeCartao.trim().length > 3 &&
      validadeCartao.length === 5 && // MM/YY
      cvvCartao.length === 3
    );
  };
  
  // Função para avançar para a próxima etapa
  const avancarEtapa = () => {
    if (etapaAtual === 1 && validarEtapa1()) {
      setEtapaAtual(2);
    } else if (etapaAtual === 2 && validarEtapa2()) {
      setEtapaAtual(3);
    } else if (etapaAtual === 3 && validarEtapa3()) {
      finalizarCadastro();
    }
  };
  
  // Função para voltar para a etapa anterior
  const voltarEtapa = () => {
    if (etapaAtual > 1) {
      setEtapaAtual(etapaAtual - 1);
    }
  };
  
  // Função para formatar o CPF/CNPJ
  const formatarDocumento = (value: string): string => {
    const numero = value.replace(/\D/g, '');
    
    if (tipoDocumento === 'cpf') {
      if (numero.length <= 3) return numero;
      if (numero.length <= 6) return `${numero.slice(0, 3)}.${numero.slice(3)}`;
      if (numero.length <= 9) return `${numero.slice(0, 3)}.${numero.slice(3, 6)}.${numero.slice(6)}`;
      return `${numero.slice(0, 3)}.${numero.slice(3, 6)}.${numero.slice(6, 9)}-${numero.slice(9, 11)}`;
    } else {
      if (numero.length <= 2) return numero;
      if (numero.length <= 5) return `${numero.slice(0, 2)}.${numero.slice(2)}`;
      if (numero.length <= 8) return `${numero.slice(0, 2)}.${numero.slice(2, 5)}.${numero.slice(5)}`;
      if (numero.length <= 12) return `${numero.slice(0, 2)}.${numero.slice(2, 5)}.${numero.slice(5, 8)}/${numero.slice(8)}`;
      return `${numero.slice(0, 2)}.${numero.slice(2, 5)}.${numero.slice(5, 8)}/${numero.slice(8, 12)}-${numero.slice(12, 14)}`;
    }
  };
  
  // Função para formatar o número do cartão
  const formatarNumeroCartao = (value: string): string => {
    const numero = value.replace(/\D/g, '');
    const chunks = [];
    
    for (let i = 0; i < numero.length; i += 4) {
      chunks.push(numero.slice(i, i + 4));
    }
    
    return chunks.join(' ');
  };
  
  // Função para formatar a validade do cartão (MM/YY)
  const formatarValidadeCartao = (value: string): string => {
    const numero = value.replace(/\D/g, '');
    
    if (numero.length <= 2) return numero;
    return `${numero.slice(0, 2)}/${numero.slice(2, 4)}`;
  };
  
  // Função para lidar com o upload de arquivo
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, setFile: React.Dispatch<React.SetStateAction<File | null>>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };
  
  // Função para finalizar o cadastro
  const finalizarCadastro = () => {
    // Aqui você enviaria os dados para a API
    console.log('Cadastro finalizado com sucesso!');
    setCadastroConcluido(true);
    
    // Redirecionar para a página de login após 3 segundos
    setTimeout(() => {
      navigate('/proponente-login');
    }, 3000);
  };
  
  // Renderização do formulário de dados pessoais
  const renderFormDadosPessoais = () => {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <RadioGroup 
            value={tipoDocumento} 
            onValueChange={setTipoDocumento}
            className="flex space-x-4 mb-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="cpf" id="cpf" />
              <Label htmlFor="cpf" className="cursor-pointer">CPF</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="cnpj" id="cnpj" />
              <Label htmlFor="cnpj" className="cursor-pointer">CNPJ</Label>
            </div>
          </RadioGroup>
          
          <Label htmlFor="documento" className="font-medium">
            {tipoDocumento.toUpperCase()}
          </Label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
              {tipoDocumento === 'cpf' ? <User className="h-4 w-4" /> : <Building className="h-4 w-4" />}
            </span>
            <Input
              id="documento"
              type="text"
              value={documento}
              onChange={(e) => setDocumento(formatarDocumento(e.target.value))}
              placeholder={tipoDocumento === 'cpf' ? '123.456.789-00' : '12.345.678/0001-90'}
              className="pl-10"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="nome" className="font-medium">
            {tipoDocumento === 'cpf' ? 'Nome Completo' : 'Razão Social'}
          </Label>
          <Input
            id="nome"
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder={tipoDocumento === 'cpf' ? 'Digite seu nome completo' : 'Digite a razão social'}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="email" className="font-medium">E-mail</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="exemplo@email.com"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="telefone" className="font-medium">Telefone</Label>
          <Input
            id="telefone"
            type="tel"
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
            placeholder="(11) 99999-9999"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="senha" className="font-medium">Senha</Label>
          <Input
            id="senha"
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            placeholder="Digite uma senha"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="confirmarSenha" className="font-medium">Confirmar Senha</Label>
          <Input
            id="confirmarSenha"
            type="password"
            value={confirmarSenha}
            onChange={(e) => setConfirmarSenha(e.target.value)}
            placeholder="Confirme sua senha"
          />
          {senha !== confirmarSenha && confirmarSenha.length > 0 && (
            <p className="text-sm text-red-500">As senhas não coincidem</p>
          )}
        </div>
      </div>
    );
  };
  
  // Renderização do formulário de documentos
  const renderFormDocumentos = () => {
    if (tipoDocumento === 'cpf') {
      return (
        <div className="p-6">
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Documentação não necessária</AlertTitle>
            <AlertDescription>
              Para cadastro com CPF, não é necessário enviar documentação adicional.
              Clique em "Avançar" para continuar com o processo de cadastro.
            </AlertDescription>
          </Alert>
          <div className="flex justify-center">
            <img src="/lovable-uploads/undraw_documents.svg" alt="Documentos" className="w-64 h-64 opacity-50" />
          </div>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Documentos Necessários</h3>
          <p className="text-sm text-gray-500">
            Anexe os documentos solicitados abaixo. Os arquivos devem estar nos formatos PDF, JPG ou PNG e ter no máximo 5MB cada.
          </p>
        </div>
        
        <div className="space-y-4">
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-[#0e7cb4]" />
                <Label className="font-medium">Estatuto</Label>
              </div>
              {estatuto && (
                <span className="flex items-center text-green-600 text-sm">
                  <Check className="h-4 w-4 mr-1" />
                  Enviado
                </span>
              )}
            </div>
            <div className="bg-gray-50 border border-dashed rounded-md p-4 text-center">
              <Input
                type="file"
                id="estatuto"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => handleFileUpload(e, setEstatuto)}
              />
              <Label htmlFor="estatuto" className="cursor-pointer flex flex-col items-center">
                <Upload className="h-6 w-6 mb-2 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">Clique para selecionar</span>
                <span className="text-xs text-gray-500">ou arraste o arquivo aqui</span>
              </Label>
              {estatuto && (
                <div className="mt-2 text-sm text-gray-600 flex items-center justify-center">
                  <File className="h-4 w-4 mr-1" />
                  {estatuto.name}
                </div>
              )}
            </div>
          </div>
          
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-[#0e7cb4]" />
                <Label className="font-medium">Contrato Social</Label>
              </div>
              {contratoSocial && (
                <span className="flex items-center text-green-600 text-sm">
                  <Check className="h-4 w-4 mr-1" />
                  Enviado
                </span>
              )}
            </div>
            <div className="bg-gray-50 border border-dashed rounded-md p-4 text-center">
              <Input
                type="file"
                id="contratoSocial"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => handleFileUpload(e, setContratoSocial)}
              />
              <Label htmlFor="contratoSocial" className="cursor-pointer flex flex-col items-center">
                <Upload className="h-6 w-6 mb-2 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">Clique para selecionar</span>
                <span className="text-xs text-gray-500">ou arraste o arquivo aqui</span>
              </Label>
              {contratoSocial && (
                <div className="mt-2 text-sm text-gray-600 flex items-center justify-center">
                  <File className="h-4 w-4 mr-1" />
                  {contratoSocial.name}
                </div>
              )}
            </div>
          </div>
          
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-[#0e7cb4]" />
                <Label className="font-medium">Cartão CNPJ</Label>
              </div>
              {cartaoCnpj && (
                <span className="flex items-center text-green-600 text-sm">
                  <Check className="h-4 w-4 mr-1" />
                  Enviado
                </span>
              )}
            </div>
            <div className="bg-gray-50 border border-dashed rounded-md p-4 text-center">
              <Input
                type="file"
                id="cartaoCnpj"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => handleFileUpload(e, setCartaoCnpj)}
              />
              <Label htmlFor="cartaoCnpj" className="cursor-pointer flex flex-col items-center">
                <Upload className="h-6 w-6 mb-2 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">Clique para selecionar</span>
                <span className="text-xs text-gray-500">ou arraste o arquivo aqui</span>
              </Label>
              {cartaoCnpj && (
                <div className="mt-2 text-sm text-gray-600 flex items-center justify-center">
                  <File className="h-4 w-4 mr-1" />
                  {cartaoCnpj.name}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Renderização do formulário de pagamento
  const renderFormPagamento = () => {
    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Caução para Participação em Pregão</h3>
          <p className="text-sm text-gray-500">
            Para garantir sua participação nos pregões eletrônicos, é necessário cadastrar um cartão de crédito para caução.
            Não haverá cobrança imediata, apenas reserva de valor em caso de arrematação.
          </p>
        </div>
        
        <div className="bg-white p-6 border rounded-lg space-y-4">
          <div className="flex items-center space-x-2 mb-4">
            <CreditCard className="h-5 w-5 text-[#0e7cb4]" />
            <span className="font-medium">Dados do Cartão de Crédito</span>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="numeroCartao" className="font-medium">Número do Cartão</Label>
            <Input
              id="numeroCartao"
              type="text"
              value={numeroCartao}
              onChange={(e) => setNumeroCartao(formatarNumeroCartao(e.target.value))}
              placeholder="1234 5678 9012 3456"
              maxLength={19}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="nomeCartao" className="font-medium">Nome no Cartão</Label>
            <Input
              id="nomeCartao"
              type="text"
              value={nomeCartao}
              onChange={(e) => setNomeCartao(e.target.value)}
              placeholder="NOME COMO ESTÁ NO CARTÃO"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="validadeCartao" className="font-medium">Validade (MM/AA)</Label>
              <Input
                id="validadeCartao"
                type="text"
                value={validadeCartao}
                onChange={(e) => setValidadeCartao(formatarValidadeCartao(e.target.value))}
                placeholder="12/25"
                maxLength={5}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cvvCartao" className="font-medium">CVV</Label>
              <Input
                id="cvvCartao"
                type="text"
                value={cvvCartao}
                onChange={(e) => setCvvCartao(e.target.value.replace(/\D/g, ''))}
                placeholder="123"
                maxLength={3}
              />
            </div>
          </div>
          
          <div className="pt-4">
            <Alert variant="default" className="bg-gray-50 border-gray-200">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Informação Importante</AlertTitle>
              <AlertDescription className="text-xs text-gray-600">
                Seus dados de cartão são criptografados e utilizados apenas para fins de caução nos pregões eletrônicos.
                Não armazenamos os dados completos do seu cartão em nossos servidores.
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </div>
    );
  };
  
  // Renderização da tela de cadastro concluído
  const renderCadastroConcluido = () => {
    return (
      <div className="text-center py-8">
        <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Cadastro Concluído!</h2>
        <p className="text-gray-600 mb-6">
          Seu cadastro foi realizado com sucesso. Você será redirecionado para a página de login.
        </p>
        <Button
          onClick={() => navigate('/proponente-login')}
          className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
        >
          Ir para Login
        </Button>
      </div>
    );
  };
  
  // Progresso do formulário (%)
  const progresso = (etapaAtual / 3) * 100;
  
  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          Cadastro de Proponente para Pregão Eletrônico
        </h1>
          
          {!cadastroConcluido ? (
            <Card className="border-gray-200">
              <CardContent className="p-6">
                {/* Indicador de Progresso */}
                <div className="mb-8">
                  <Progress value={progresso} className="h-2" />
                  
                  <div className="flex justify-between mt-2">
                    <span className={`text-sm ${etapaAtual >= 1 ? 'text-[#0e7cb4] font-medium' : 'text-gray-400'}`}>
                      Dados Pessoais
                    </span>
                    <span className={`text-sm ${etapaAtual >= 2 ? 'text-[#0e7cb4] font-medium' : 'text-gray-400'}`}>
                      Documentos
                    </span>
                    <span className={`text-sm ${etapaAtual >= 3 ? 'text-[#0e7cb4] font-medium' : 'text-gray-400'}`}>
                      Caução
                    </span>
                  </div>
                </div>
                
                {/* Conteúdo do formulário */}
                <div className="mb-8">
                  {etapaAtual === 1 && renderFormDadosPessoais()}
                  {etapaAtual === 2 && renderFormDocumentos()}
                  {etapaAtual === 3 && renderFormPagamento()}
                </div>
                
                {/* Botões de navegação */}
                <div className="flex justify-between">
                  <Button
                    type="button"
                    onClick={voltarEtapa}
                    disabled={etapaAtual === 1}
                    variant="outline"
                    className="flex items-center space-x-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Voltar</span>
                  </Button>
                  
                  <Button
                    type="button"
                    onClick={avancarEtapa}
                    disabled={
                      (etapaAtual === 1 && !validarEtapa1()) ||
                      (etapaAtual === 2 && !validarEtapa2()) ||
                      (etapaAtual === 3 && !validarEtapa3())
                    }
                    className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white flex items-center space-x-2"
                  >
                    <span>{etapaAtual === 3 ? 'Finalizar' : 'Avançar'}</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-gray-200">
              <CardContent className="p-6">
                {renderCadastroConcluido()}
              </CardContent>
            </Card>
          )}
      </div>
    </MainLayout>
  );
};

export default ProponenteRegistro; 